import { useCallback, useEffect, useRef, useState } from "react";

export function useAbortableFetch<T>(
  fetchFn: (signal: AbortSignal) => Promise<T>,
  onData: (data: T) => void,
  deps: readonly unknown[],
): {
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
} {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchRef = useRef(fetchFn);
  const onDataRef = useRef(onData);
  fetchRef.current = fetchFn;
  onDataRef.current = onData;

  const refetch = useCallback(async () => {
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchRef.current(abort.signal);
      if (abort.signal.aborted) return;
      onDataRef.current(result);
    } catch (err) {
      if (abort.signal.aborted) return;
      setError(err);
    } finally {
      if (!abort.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-provided deps spread intentionally
  }, [refetch, ...deps]);

  return { loading, error, refetch };
}
